import React, { useRef, useEffect, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import {
  PrecisionManufacturing as MachineIcon,
  LocationOnOutlined as LocationIcon,
  AttachFileOutlined as FileIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { dt, StatusBadgeColorConfig } from "../../../../shared/themes/redesignTokens";
import { EnrichedServiceReport } from "../../store/serviceReportsSlice";
import { WorkOrderStatusCode } from "../../models/models";
import { ServiceRequestStatusCode } from "../../../servicing/models/models";
import { ServiceReportFile } from "../../../machinery/models/ServiceReportsModel";
import useTranslation from "../../../../shared/hooks/useTranslation";
import RedesignStatusBadge from "../../../../shared/components/redesign/RedesignStatusBadge";
import { ImagePreviewStrip, useGalleryImages } from "../../../../shared/components/gallery";
import ReportFile from "../../../../shared/ReportFile";
import serviceReportsApi from "../../api/serviceReportsApi";
import ServiceReportOverflowMenu, { SrAction } from "./ServiceReportOverflowMenu";
import UrgencyPopover from "./UrgencyPopover";
import InlineNoteSection from "./InlineNoteSection";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import { statusTranslationKeys } from "../WorkOrderStatusBadge";
import { getUiLocale } from "../../../../shared/utils/relativeTime";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

dayjs.extend(relativeTime);
dayjs.extend(duration);

// ─── Urgency config (solid filled pill badges) ──────────────

interface UrgencyPillConfig {
  labelKey: string;
  bg: string;
}

const URGENCY_MAP: Record<number, UrgencyPillConfig> = {
  1: { labelKey: "serviceReports.urgency.critical", bg: "#D4572B" },
  2: { labelKey: "serviceReports.urgency.high", bg: "#E8A817" },
  4: { labelKey: "serviceReports.urgency.normal", bg: "#3B82F6" },
};

const URGENCY_INFO: UrgencyPillConfig = {
  labelKey: "serviceReports.urgency.info",
  bg: "#8C9099",
};

// ─── Additional badge color configs ─────────────────────────
const ESCALATED_COLOR: StatusBadgeColorConfig = { bg: "#FEF5E7", text: "#E67E22", border: "#FDEBD0" };
const OFFERS_COLOR: StatusBadgeColorConfig = { bg: "#FEF9E7", text: "#E8A817", border: "#F9E79F" };
const DRAFT_RFS_COLOR: StatusBadgeColorConfig = { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" };

// ─── WO status colors ───────────────────────────────────────

const WO_STATUS_COLORS: Record<number, StatusBadgeColorConfig> = {
  [WorkOrderStatusCode.Created]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [WorkOrderStatusCode.Assigned]: { bg: "#EBF5FB", text: "#2980B9", border: "#AED6F1" },
  [WorkOrderStatusCode.Scheduled]: { bg: "#F5EEF8", text: "#8E44AD", border: "#D2B4DE" },
  [WorkOrderStatusCode.InProgress]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [WorkOrderStatusCode.OnHold]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [WorkOrderStatusCode.Completed]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [WorkOrderStatusCode.Verified]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [WorkOrderStatusCode.Cancelled]: { bg: "#F4F6F9", text: "#9CA3AF", border: "#D1D5DB" },
};

// ─── RFS status config ──────────────────────────────────────

const RFS_STATUS_LABEL_KEYS: Record<number, string> = {
  1: "serviceReports.rfsStatus.draft",
  2: "serviceReports.rfsStatus.openForOffers",
  3: "serviceReports.rfsStatus.negotiating",
  4: "serviceReports.rfsStatus.inProgress",
  5: "serviceReports.rfsStatus.completed",
  6: "serviceReports.rfsStatus.archived",
  7: "serviceReports.rfsStatus.awaitingConfirmation",
  8: "serviceReports.rfsStatus.completionConfirmation",
};

const RFS_STATUS_COLORS: Record<number, StatusBadgeColorConfig> = {
  [ServiceRequestStatusCode.draft]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [ServiceRequestStatusCode.openForOffers]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [ServiceRequestStatusCode.negotiating]: { bg: "#F5EEF8", text: "#8E44AD", border: "#D2B4DE" },
  [ServiceRequestStatusCode.inProgress]: { bg: "#EBF5FB", text: "#2980B9", border: "#AED6F1" },
  [ServiceRequestStatusCode.completed]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [ServiceRequestStatusCode.archived]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [ServiceRequestStatusCode.awaitingProviderConfirmation]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [ServiceRequestStatusCode.awaitingCompletionConfirmation]: { bg: "#EBF5FB", text: "#2980B9", border: "#AED6F1" },
};

// ─── Helpers ────────────────────────────────────────────────

/** Format SR number with cosmetic prefix for old 9-digit format. */
function formatSrNumber(num: string | undefined): string {
  if (!num) return "SR-???";
  if (num.startsWith("SR-")) return num;
  const short = num.length > 4 ? num.slice(-4) : num;
  return `SR-${short}`;
}

function timeAgo(dateStr: string | Date | undefined): string {
  if (!dateStr) return "";
  return dayjs(dateStr).fromNow();
}

/** Extract 1-2 character initials from a name. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic color from initials for the avatar background. */
function avatarColor(name: string): string {
  const colors = [
    "#D4572B", "#E8A817", "#2D8C4E", "#3B82F6",
    "#8E44AD", "#C0392B", "#16A085", "#D35400",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// ─── Props ──────────────────────────────────────────────────

interface ServiceReportCardProps {
  item: EnrichedServiceReport;
  isPhone: boolean;
  isTablet?: boolean;
  hasServicingPermission: boolean;
  onCreateWorkOrder: (item: EnrichedServiceReport) => void;
  onCreateServiceRequest: (item: EnrichedServiceReport) => void;
  onViewOnBoard: (woId: string) => void;
  onViewInServiceHub: (rfsId: string) => void;
  onOverflowAction: (action: SrAction, item: EnrichedServiceReport) => void;
  onUrgencyChange: (item: EnrichedServiceReport, newUrgency: number) => void;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  /** Bulk select mode */
  selectMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

/** Format a duration from openDate to now as "Xd Yh Zm". */
function formatOpenDuration(openDate: string | Date | undefined): string {
  if (!openDate) return "";
  const diff = dayjs.duration(dayjs().diff(dayjs(openDate)));
  const days = Math.floor(diff.asDays());
  const hours = diff.hours();
  const minutes = diff.minutes();
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(" ");
}

// ─── Component ──────────────────────────────────────────────

const ServiceReportCard: React.FC<ServiceReportCardProps> = ({
  item,
  isPhone,
  isTablet = false,
  hasServicingPermission,
  onCreateWorkOrder,
  onCreateServiceRequest,
  onViewOnBoard,
  onViewInServiceHub,
  onOverflowAction,
  onUrgencyChange,
  index,
  isExpanded,
  onToggle,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const { translate } = useTranslation("operations");
  const navigate = useNavigate();
  const { sr, tab, linkedWo, linkedRfs, isRfsDraft } = item;

  // Urgency popover state
  const [urgencyPopoverOpen, setUrgencyPopoverOpen] = useState(false);
  const urgencyBadgeRef = useRef<HTMLDivElement>(null);
  const canChangeUrgency = tab === "untriaged" || tab === "internal";

  // Dynamic height for smooth expand/collapse animation
  // Uses ResizeObserver to track content height changes (e.g. lazy-loaded attachments)
  const expandedRef = useRef<HTMLDivElement>(null);
  const [expandedHeight, setExpandedHeight] = useState(0);

  useEffect(() => {
    if (!isExpanded || !expandedRef.current) return;
    setExpandedHeight(expandedRef.current.scrollHeight);

    const observer = new ResizeObserver(() => {
      if (expandedRef.current) {
        setExpandedHeight(expandedRef.current.scrollHeight);
      }
    });
    observer.observe(expandedRef.current);
    return () => observer.disconnect();
  }, [isExpanded]);

  const urgencyPill = sr.urgency ? (URGENCY_MAP[sr.urgency] ?? URGENCY_INFO) : URGENCY_INFO;
  const isResolved = tab === "resolved";

  const machineNameBase =
    [sr.manufacturerName, sr.machineModel].filter(Boolean).join(" ") ||
    sr.machineInternalNumber ||
    translate("serviceReports.unknownMachine");

  const locationName = sr.parentLocationName
    ? `${sr.parentLocationName}, ${sr.locationName}`
    : sr.locationName || "";
  const reporterName = sr.createdByName || translate("serviceReports.unknownReporter");
  const fileCount = sr.fileCount ?? sr.files?.length ?? 0;
  const dateStr = sr.openDate || sr.createdDate;

  // Check if there's any expandable content
  const hasDescription = Boolean(sr.description);
  const hasNotes = Boolean(sr.notes);
  const hasAlarmCode = Boolean(sr.alarmCode);
  const hasFiles = (sr.files?.length ?? 0) > 0 || (sr.fileCount ?? 0) > 0;
  const hasExpandableContent = hasDescription || hasNotes || hasAlarmCode || hasFiles;

  // In select mode: click toggles selection, not expand
  const handleCardClick = selectMode
    ? onToggleSelect
    : (hasExpandableContent ? onToggle : undefined);

  return (
    <Box
      sx={{
        backgroundColor: selectMode && isSelected ? "#FDF5F2" : "#FFFFFF",
        borderRadius: dt.radius,
        overflow: "hidden",
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: isExpanded
          ? "0 2px 8px rgba(0,0,0,0.05), 0 6px 20px rgba(0,0,0,0.03)"
          : dt.cardShadow,
        transition: "all 0.2s ease",
        animation: `fadeSlideUp 0.35s ease ${index * 0.04}s both`,
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: isExpanded ? "none" : "translateY(-1px)",
        },
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        ...(isResolved && !selectMode && { opacity: 0.85 }),
        ...(selectMode && isSelected && {
          outline: "2px solid rgba(212,87,43,0.25)",
        }),
      }}
    >
      {/* Select mode checkbox — spans full card height */}
      {selectMode && (
        <Box
          onClick={handleCardClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            flexShrink: 0,
            pl: "8px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <Box
            sx={{
              width: "22px",
              height: "22px",
              borderRadius: "50%",
              border: isSelected ? "none" : "2px solid #D1CCC4",
              backgroundColor: isSelected ? "#D4572B" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}
          >
            {isSelected && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2.5 6L5 8.5L9.5 3.5"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </Box>
        </Box>
      )}

      {/* Left urgency stripe — spans full card height (collapsed + expanded) */}
      <Box
        sx={{
          width: "4px",
          alignSelf: "stretch",
          backgroundColor: urgencyPill.bg,
          flexShrink: 0,
          borderRadius: selectMode ? "0" : `${dt.radius} 0 0 ${dt.radius}`,
        }}
      />

      {/* Content wrapper — collapsed + expanded sections */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Collapsed card content — clickable to expand (or select in select mode) */}
        <Box
          onClick={handleCardClick}
          sx={{
            cursor: selectMode ? "pointer" : (hasExpandableContent ? "pointer" : "default"),
            userSelect: selectMode ? "none" : (hasExpandableContent ? "none" : "auto"),
          }}
        >
          {/* Card body */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              padding: isPhone ? "12px" : isTablet ? "16px 18px" : "20px 24px",
              display: "flex",
              flexDirection: "column",
              gap: isPhone ? "6px" : isTablet ? "8px" : "10px",
            }}
          >
          {/* ── Row 1: Header — SR number + urgency + linked item | Actions + Chevron ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              flexWrap: isPhone ? "wrap" : "nowrap",
            }}
          >
            {/* Left: SR number + badges + linked item info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              {/* SR number — clickable link to SR detail page */}
              <Box
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (sr.machineId && sr.number) {
                    navigate(`/machinery/${sr.machineId}/service-reports/${sr.number}`);
                  }
                }}
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: isResolved ? "#9B9B9B" : dt.textSecondary,
                  letterSpacing: "0.3px",
                  lineHeight: "16px",
                  flexShrink: 0,
                  cursor: sr.machineId && sr.number ? "pointer" : "default",
                  "&:hover": sr.machineId && sr.number ? {
                    textDecoration: "underline",
                    color: "#2980B9",
                  } : {},
                }}
              >
                {formatSrNumber(sr.number)}
              </Box>

              {/* Urgency badge — solid filled pill, clickable on untriaged/internal */}
              <Box
                ref={urgencyBadgeRef}
                onClick={canChangeUrgency ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  setUrgencyPopoverOpen((prev) => !prev);
                } : undefined}
                sx={{
                  backgroundColor: urgencyPill.bg,
                  color: "#FFFFFF",
                  fontFamily: dt.font,
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  lineHeight: "14px",
                  flexShrink: 0,
                  cursor: canChangeUrgency ? "pointer" : "default",
                  transition: "opacity 0.15s ease, transform 0.15s ease",
                  ...(canChangeUrgency && {
                    "&:hover": {
                      opacity: 0.85,
                    },
                  }),
                }}
              >
                {translate(urgencyPill.labelKey)}
              </Box>

              {/* Quick urgency change popover */}
              {canChangeUrgency && (
                <UrgencyPopover
                  anchorEl={urgencyBadgeRef.current}
                  open={urgencyPopoverOpen}
                  currentUrgency={sr.urgency ?? 0}
                  onSelect={(newUrgency) => {
                    setUrgencyPopoverOpen(false);
                    onUrgencyChange(item, newUrgency);
                  }}
                  onClose={() => setUrgencyPopoverOpen(false)}
                  translate={translate}
                />
              )}

              {/* Alarm code badge — amber left-bar accent, inline in header */}
              {hasAlarmCode && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                    padding: "2px 8px",
                    gap: "6px",
                    backgroundColor: "#FEF9E7",
                    borderLeft: "3px solid #F39C12",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#D68910",
                      letterSpacing: "0.5px",
                      lineHeight: "16px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sr.alarmCode}
                  </Box>
                </Box>
              )}

              {/* Resolved: SOLVED badge + close date in header row */}
              {isResolved && (
                <>
                  <Box sx={{
                    backgroundColor: "#E8F5EC",
                    borderLeft: "3px solid #2D8C4E",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                    padding: "2px 8px",
                    display: "inline-flex",
                    flexShrink: 0,
                  }}>
                    <Box sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#2D8C4E",
                      letterSpacing: "0.05em",
                    }}>SOLVED</Box>
                  </Box>
                  {sr.closeDate && (
                    <Box sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: "#9B9B9B",
                      flexShrink: 0,
                    }}>
                      {translate("serviceReports.closed")} {dayjs(sr.closeDate).fromNow()}
                    </Box>
                  )}
                </>
              )}

              {/* Internal tab: arrow + WO number + WO status */}
              {tab === "internal" && linkedWo && (
                <>
                  <ArrowIcon sx={{ fontSize: "12px", color: "#D1CCC4", flexShrink: 0 }} />
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#3B82F6",
                      letterSpacing: "0.2px",
                      flexShrink: 0,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewOnBoard(linkedWo.id); }}
                  >
                    {linkedWo.number}
                  </Box>
                  <RedesignStatusBadge
                    label={(statusTranslationKeys[linkedWo.workOrderStatusId]
                      ? translate(statusTranslationKeys[linkedWo.workOrderStatusId])
                      : linkedWo.workOrderStatusName || translate("statuses.created")
                    ).toUpperCase()}
                    colorConfig={WO_STATUS_COLORS[linkedWo.workOrderStatusId] || WO_STATUS_COLORS[1]}
                  />
                  {/* Escalated badge — if WO has a linked RFS */}
                  {linkedWo.serviceRequestId && (
                    <RedesignStatusBadge
                      label={translate("serviceReports.escalated")}
                      colorConfig={ESCALATED_COLOR}
                    />
                  )}
                </>
              )}

              {/* External tab: arrow + RFS number + RFS status + offers badge */}
              {tab === "external" && linkedRfs && (
                <>
                  <ArrowIcon sx={{ fontSize: "12px", color: "#D1CCC4", flexShrink: 0 }} />
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#E8A817",
                      letterSpacing: "0.2px",
                      flexShrink: 0,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewInServiceHub(linkedRfs.id); }}
                  >
                    {linkedRfs.internalNumber}
                  </Box>
                  <RedesignStatusBadge
                    label={RFS_STATUS_LABEL_KEYS[linkedRfs.statusCode] ? translate(RFS_STATUS_LABEL_KEYS[linkedRfs.statusCode]) : "---"}
                    colorConfig={RFS_STATUS_COLORS[linkedRfs.statusCode] || RFS_STATUS_COLORS[1]}
                  />
                  {/* Offers received badge — G3: placed in header row per design */}
                  {(linkedRfs.offersCount ?? 0) > 0 && (
                    <RedesignStatusBadge
                      label={translate("serviceReports.offersReceivedCount", { count: linkedRfs.offersCount ?? 0 })}
                      colorConfig={OFFERS_COLOR}
                    />
                  )}
                </>
              )}

              {/* Draft RFS indicator (Untriaged tab) */}
              {isRfsDraft && (
                <RedesignStatusBadge
                  label={translate("serviceReports.draftRfs")}
                  colorConfig={DRAFT_RFS_COLOR}
                />
              )}
            </Box>

            {/* Right: Action buttons + overflow menu + chevron (desktop only — hidden in select mode, phone renders below) */}
            {!isPhone && !selectMode && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <CardActions
                    tab={tab}
                    item={item}
                    linkedWo={linkedWo}
                    linkedRfs={linkedRfs}
                    hasServicingPermission={hasServicingPermission}
                    onCreateWorkOrder={onCreateWorkOrder}
                    onCreateServiceRequest={onCreateServiceRequest}
                    onViewOnBoard={onViewOnBoard}
                    onViewInServiceHub={onViewInServiceHub}
                    translate={translate}
                  />
                </Box>
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <ServiceReportOverflowMenu
                    item={item}
                    onAction={onOverflowAction}
                    translate={translate}
                  />
                </Box>
                {hasExpandableContent && (
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      borderRadius: dt.radiusSm,
                      backgroundColor: isExpanded ? "rgba(52,152,219,0.08)" : "#F4F6F9",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: isExpanded ? "rgba(52,152,219,0.12)" : "#EDEFF3" },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: dt.font,
                        fontSize: "14px",
                        lineHeight: "18px",
                        color: isExpanded ? "#3498DB" : "#9B9B9B",
                        transition: "color 0.2s ease",
                        userSelect: "none",
                      }}
                    >
                      {isExpanded ? "\u25B4" : "\u25BE"}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* ── Row 2: Title ── */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: isPhone ? "14px" : "16px",
              fontWeight: 600,
              color: isResolved ? "#3D3D3D" : dt.textPrimary,
              letterSpacing: "-0.2px",
              lineHeight: "20px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {sr.title || translate("serviceReports.untitled")}
          </Box>

          {/* ── Row 3: Machine + Location ── */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: isPhone ? "8px" : "16px", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
              <MachineIcon sx={{ fontSize: "14px", color: "#8C9099", flexShrink: 0 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0, minWidth: 0 }}>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: dt.textSecondary,
                    lineHeight: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {machineNameBase}
                </Box>
                {sr.machineSerialNumber && (
                  <>
                    <Box component="span" sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted, mx: "3px" }}>{"\u00B7"}</Box>
                    <Box
                      component="span"
                      sx={{
                        fontFamily: dt.fontMono,
                        fontSize: "11px",
                        fontWeight: 400,
                        color: dt.textMuted,
                        lineHeight: "16px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      SN:{sr.machineSerialNumber}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
            {locationName && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "3px", minWidth: 0 }}>
                <LocationIcon sx={{ fontSize: "14px", color: "#8C9099", flexShrink: 0 }} />
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 400,
                    color: dt.textSecondary,
                    lineHeight: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {locationName}
                </Box>
              </Box>
            )}
          </Box>

          {/* ── Resolved: Solving description row ── */}
          {isResolved && sr.solvingDescription && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 400,
                color: "#6B7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {sr.solvingDescription}
            </Box>
          )}

          {/* ── Row 4: Reporter avatar + name + time + files + opened + open for ── */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
            {/* Avatar + name */}
            {tab === "internal" && linkedWo?.assignedToUserName ? (
              <ReporterChip label={translate("serviceReports.assignedTo")} name={linkedWo.assignedToUserName} />
            ) : tab === "external" && linkedRfs ? (
              <ExternalMetaRow linkedRfs={linkedRfs} translate={translate} />
            ) : (
              <ReporterChip name={reporterName} />
            )}

            {/* Time ago — JetBrains Mono */}
            {dateStr && (
              <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", fontWeight: 400, color: dt.textMuted, lineHeight: "16px" }}>
                {timeAgo(dateStr)}
              </Box>
            )}

            {/* File count */}
            {fileCount > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                <FileIcon sx={{ fontSize: "13px", color: dt.textMuted }} />
                <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted, lineHeight: "16px" }}>
                  {translate("serviceReports.fileCount", { count: fileCount })}
                </Box>
              </Box>
            )}

            {/* Dot separator before opened */}
            {sr.openDate && (
              <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted, lineHeight: "16px" }}>{"\u00B7"}</Box>
            )}

            {/* Red dot + "Opened" + date */}
            {sr.openDate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#C0392B", flexShrink: 0 }} />
                <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: dt.textMuted, lineHeight: "16px" }}>
                  {translate("serviceReports.expanded.opened")}
                </Box>
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", fontWeight: 400, color: dt.textMuted, lineHeight: "16px" }}>
                  {formatUiDate(sr.openDate, "dateTime")}
                </Box>
              </Box>
            )}

            {/* Dot separator before open for */}
            {sr.openDate && sr.statusCode !== 2 && (
              <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted, lineHeight: "16px" }}>{"\u00B7"}</Box>
            )}

            {/* Amber dot + "Open for" + duration */}
            {sr.openDate && sr.statusCode !== 2 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#F39C12", flexShrink: 0 }} />
                <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: dt.textMuted, lineHeight: "16px" }}>
                  {translate("serviceReports.expanded.openFor")}
                </Box>
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", fontWeight: 500, color: "#D68910", lineHeight: "16px" }}>
                  {formatOpenDuration(sr.openDate)}
                </Box>
              </Box>
            )}
          </Box>

          {/* ── Resolved: Linked WO/RFS reference row ── */}
          {isResolved && linkedWo && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "2px" }}>
              <ArrowIcon sx={{ fontSize: "12px", color: "#D1CCC4" }} />
              <Box
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#3B82F6",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewOnBoard(linkedWo.id); }}
              >
                {linkedWo.number}
              </Box>
              {(linkedWo.workOrderStatusId === WorkOrderStatusCode.Completed ||
                linkedWo.workOrderStatusId === WorkOrderStatusCode.Verified) && (
                <Box sx={{
                  backgroundColor: "#E8F5EC",
                  borderRadius: "10px",
                  padding: "2px 8px",
                  display: "inline-flex",
                }}>
                  <Box sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#2D8C4E",
                  }}>
                    {translate("serviceReports.completed")}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {isResolved && linkedRfs && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "2px" }}>
              <ArrowIcon sx={{ fontSize: "12px", color: "#D1CCC4" }} />
              <Box
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#3B82F6",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewInServiceHub(linkedRfs.id); }}
              >
                {linkedRfs.internalNumber}
              </Box>
              {(linkedRfs.statusCode === ServiceRequestStatusCode.completed) && (
                <Box sx={{
                  backgroundColor: "#E8F5EC",
                  borderRadius: "10px",
                  padding: "2px 8px",
                  display: "inline-flex",
                }}>
                  <Box sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#2D8C4E",
                  }}>
                    {translate("serviceReports.completed")}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ── Phone: action buttons + overflow menu + chevron below content (hidden in select mode) ── */}
          {isPhone && !selectMode && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "4px" }}>
              <Box sx={{ flex: 1 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <CardActions
                  tab={tab}
                  item={item}
                  linkedWo={linkedWo}
                  linkedRfs={linkedRfs}
                  hasServicingPermission={hasServicingPermission}
                  onCreateWorkOrder={onCreateWorkOrder}
                  onCreateServiceRequest={onCreateServiceRequest}
                  onViewOnBoard={onViewOnBoard}
                  onViewInServiceHub={onViewInServiceHub}
                  translate={translate}
                  isPhone
                />
              </Box>
              <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <ServiceReportOverflowMenu
                  item={item}
                  onAction={onOverflowAction}
                  translate={translate}
                />
              </Box>
              {hasExpandableContent && (
                <Box
                  sx={{
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    borderRadius: "6px",
                    backgroundColor: isExpanded ? "rgba(52,152,219,0.08)" : "#F4F6F9",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: isExpanded ? "#3498DB" : "#9B9B9B",
                      userSelect: "none",
                    }}
                  >
                    {isExpanded ? "\u25B4" : "\u25BE"}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          </Box>
        </Box>

        {/* ── Expanded section — slides open below collapsed content ── */}
        {hasExpandableContent && (
          <div
            style={{
              overflow: "hidden",
              transition: "max-height 200ms ease-out, opacity 200ms ease-out",
              maxHeight: isExpanded ? `${expandedHeight}px` : "0px",
              opacity: isExpanded ? 1 : 0,
            }}
          >
            <div ref={expandedRef}>
              <ExpandedSection
                sr={sr}
                isPhone={isPhone}
                translate={translate}
                navigate={navigate}
                isResolved={isResolved}
              />
            </div>
          </div>
        )}
      </Box>
    </Box>
  );
};

// ─── Sub-components ─────────────────────────────────────────

/** Initials avatar + name label. */
const ReporterChip: React.FC<{ label?: string; name: string }> = ({ label, name }) => {
  const initials = getInitials(name);
  const bgColor = avatarColor(name);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      {/* Avatar circle — 22px per Paper */}
      <Box
        sx={{
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          backgroundColor: bgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "9px",
            fontWeight: 600,
            color: "#FFFFFF",
            lineHeight: "12px",
            letterSpacing: "0.3px",
          }}
        >
          {initials}
        </Box>
      </Box>
      {/* Name text */}
      <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 400, color: dt.textSecondary, lineHeight: "16px" }}>
        {label && (
          <Box component="span" sx={{ color: "#B8B5AF", mr: "4px" }}>
            {label}
          </Box>
        )}
        {name}
      </Box>
    </Box>
  );
};

/** External tab: provider info or best offer info. */
const ExternalMetaRow: React.FC<{
  linkedRfs: any;
  translate: (key: string, options?: Record<string, unknown>) => string;
}> = ({ linkedRfs, translate }) => {
  // Show best offer or provider info
  if (linkedRfs.acceptedOffer?.providerName) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Box
          sx={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#2D8C4E",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box sx={{ fontFamily: dt.font, fontSize: "8px", fontWeight: 600, color: "#F7F5F0" }}>
            {getInitials(linkedRfs.acceptedOffer.providerName)}
          </Box>
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: "#8C9099" }}>
          <Box component="span" sx={{ color: "#2D8C4E", fontWeight: 600, mr: "4px" }}>
            {translate("serviceReports.provider")}
          </Box>
          {linkedRfs.acceptedOffer.providerName}
        </Box>
      </Box>
    );
  }

  if (linkedRfs.lowestOfferPrice != null && linkedRfs.lowestOfferPrice > 0 && (linkedRfs.offersCount ?? 0) > 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Box
          sx={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#E8A817",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box sx={{ fontFamily: dt.font, fontSize: "9px", fontWeight: 600, color: "#F7F5F0" }}>
            {"$"}
          </Box>
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: "#8C9099" }}>
          <Box component="span" sx={{ color: "#2D8C4E", fontWeight: 600, mr: "4px" }}>
            {translate("serviceReports.bestOffer")}
          </Box>
          <Box component="span" sx={{ fontFamily: dt.fontMono, fontWeight: 600, color: "#2D8C4E" }}>
            {"\u20AC"}{linkedRfs.lowestOfferPrice.toLocaleString(getUiLocale())}
          </Box>
          <Box component="span" sx={{ ml: "8px", color: "#B8B5AF" }}>
            {translate("serviceReports.offerCount", { count: linkedRfs.offersCount ?? 0 })}
          </Box>
        </Box>
      </Box>
    );
  }

  // Fallback: show offer count or "Sent" info
  const offerCount = linkedRfs.offersCount ?? 0;
  if (offerCount > 0) {
    return (
      <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", color: "#E8A817", fontWeight: 600 }}>
        {translate("serviceReports.offersReceivedCount", { count: offerCount })}
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: "#B8B5AF" }}>
      {translate("serviceReports.sent")} {timeAgo(linkedRfs.createdDate)}
    </Box>
  );
};

/** Action buttons / links that appear at the right of the card header. */
const CardActions: React.FC<{
  tab: string;
  item: EnrichedServiceReport;
  linkedWo?: any;
  linkedRfs?: any;
  hasServicingPermission: boolean;
  onCreateWorkOrder: (item: EnrichedServiceReport) => void;
  onCreateServiceRequest: (item: EnrichedServiceReport) => void;
  onViewOnBoard: (woId: string) => void;
  onViewInServiceHub: (rfsId: string) => void;
  translate: (key: string) => string;
  isPhone?: boolean;
}> = ({
  tab,
  item,
  linkedWo,
  linkedRfs,
  hasServicingPermission,
  onCreateWorkOrder,
  onCreateServiceRequest,
  onViewOnBoard,
  onViewInServiceHub,
  translate,
  isPhone,
}) => {
  // ── Untriaged: CTA buttons — Paper design: terracotta filled + outlined ──
  if (tab === "untriaged") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: isPhone ? "column" : "row",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        <Button
          size="small"
          variant="contained"
          startIcon={<Box component="span" sx={{ fontSize: "14px", lineHeight: "18px" }}>{"\u2295"}</Box>}
          onClick={() => onCreateWorkOrder(item)}
          sx={{
            fontFamily: dt.font,
            fontSize: "13px !important",
            fontWeight: "600 !important",
            textTransform: "none",
            backgroundColor: "#D4572B !important",
            color: "#FFFFFF !important",
            borderRadius: dt.radiusSm + " !important",
            padding: "8px 18px",
            minHeight: "34px",
            letterSpacing: "0.2px",
            whiteSpace: "nowrap",
            boxShadow: "none !important",
            "&:hover": {
              backgroundColor: "#B84A24 !important",
            },
            width: isPhone ? "100%" : "auto",
          }}
        >
          {translate("serviceReports.createWorkOrder")}
        </Button>
        {hasServicingPermission && (
          <Box
            component="button"
            onClick={() => onCreateServiceRequest(item)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "none",
              border: `1px solid #E8E5DF`,
              color: "#D4572B",
              backgroundColor: "#FFFFFF",
              borderRadius: dt.radiusSm,
              padding: "8px 18px",
              minHeight: "34px",
              letterSpacing: "0.2px",
              whiteSpace: "nowrap",
              cursor: "pointer",
              outline: "none",
              "&:hover": {
                borderColor: "#D1CCC4",
                color: "#B84A24",
                backgroundColor: "#FAFAFA",
              },
              width: isPhone ? "100%" : "auto",
              justifyContent: "center",
            }}
          >
            <Box component="span" sx={{ fontSize: "13px", lineHeight: "16px", color: "inherit" }}>{"\u2192"}</Box>
            {translate("serviceReports.createServiceRequest")}
          </Box>
        )}
      </Box>
    );
  }

  // ── Internal: "View on Board ↗" outlined button — Paper: Sora 13px, terracotta ──
  if (tab === "internal" && linkedWo) {
    return (
      <Box
        onClick={() => onViewOnBoard(linkedWo.id)}
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 500,
          color: "#D4572B",
          flexShrink: 0,
          whiteSpace: "nowrap",
          cursor: "pointer",
          letterSpacing: "0.1px",
          border: "1px solid #E8E5DF",
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          padding: "8px 18px",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          "&:hover": {
            color: "#B84A24",
            borderColor: "#D1CCC4",
            backgroundColor: "#FAFAFA",
          },
        }}
      >
        {translate("serviceReports.viewOnBoard")} {"\u2197"}
      </Box>
    );
  }

  // ── External: "View in Service Hub ↗" outlined button — Paper: Sora 13px, terracotta ──
  if (tab === "external" && linkedRfs) {
    return (
      <Box
        onClick={() => onViewInServiceHub(linkedRfs.id)}
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 500,
          color: "#D4572B",
          flexShrink: 0,
          whiteSpace: "nowrap",
          cursor: "pointer",
          letterSpacing: "0.1px",
          border: "1px solid #E8E5DF",
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          padding: "8px 18px",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          "&:hover": {
            color: "#B84A24",
            borderColor: "#D1CCC4",
            backgroundColor: "#FAFAFA",
          },
        }}
      >
        {translate("serviceReports.viewInServiceHub")} {"\u2197"}
      </Box>
    );
  }

  return null;
};

// ─── Expanded Section ───────────────────────────────────────

interface ExpandedSectionProps {
  sr: EnrichedServiceReport["sr"];
  isPhone: boolean;
  translate: (key: string) => string;
  navigate: ReturnType<typeof useNavigate>;
  isResolved?: boolean;
}

/** Section header — Sora 11px, 700 weight, uppercase, blue #3498DB (or custom color), 1.2px letter-spacing */
const SectionLabel: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
  <Box
    sx={{
      fontFamily: dt.font,
      fontSize: "11px",
      fontWeight: 700,
      color: color || "#3498DB",
      textTransform: "uppercase",
      letterSpacing: "1.2px",
      lineHeight: "14px",
      marginBottom: "8px",
    }}
  >
    {children}
  </Box>
);

/** Detect image files by extension or content prefix. */
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isImageFile = (file: ServiceReportFile): boolean =>
  IMAGE_EXTENSIONS.test(file.name) || file.content?.startsWith("data:image/");

/** Sub-component: renders attachments with gallery (lazy-loaded file content). */
const AttachmentsSection: React.FC<{
  sr: EnrichedServiceReport["sr"];
  translate: (key: string, options?: Record<string, unknown>) => string;
}> = ({ sr, translate }) => {
  const [detailFiles, setDetailFiles] = useState<ServiceReportFile[] | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);

  const fileCount = sr.fileCount ?? sr.files?.length ?? 0;

  // Determine whether we already have file content from the company endpoint
  const existingFilesWithContent = sr.files?.filter((f) => Boolean(f.content)) ?? [];
  const needsFetch = fileCount > 0 && existingFilesWithContent.length === 0;

  // Lazy-load full file content from the per-machine SR endpoint
  useEffect(() => {
    if (!needsFetch || detailFiles || filesLoading) return;
    if (!sr.machineId || !sr.number) return;

    setFilesLoading(true);
    setFetchFailed(false);
    serviceReportsApi
      .getServiceReportDetail(sr.machineId, sr.number)
      .then((detail) => {
        if (detail?.files && detail.files.length > 0) {
          setDetailFiles(detail.files);
        } else {
          setFetchFailed(true);
        }
      })
      .catch(() => setFetchFailed(true))
      .finally(() => setFilesLoading(false));
  }, [needsFetch, sr.machineId, sr.number, detailFiles, filesLoading]);

  // Use either lazy-loaded files or the existing files with content
  const files = needsFetch ? detailFiles : existingFilesWithContent.length > 0 ? sr.files : null;

  const imageFiles = files?.filter(isImageFile) ?? [];
  const nonImageFiles = files?.filter((f) => !isImageFile(f)) ?? [];
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  return (
    <Box>
      <SectionLabel>{translate("serviceReports.expanded.attachments")}</SectionLabel>

      {filesLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CircularProgress size={14} sx={{ color: dt.textMuted }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted }}>
            {translate("serviceReports.expanded.loadingAttachments")}
          </Box>
        </Box>
      ) : files && files.length > 0 ? (
        <>
          {/* Image thumbnails — with lightbox */}
          {galleryImages.length > 0 && (
            <Box sx={{ mb: nonImageFiles.length > 0 ? 1.5 : 0 }}>
              <ImagePreviewStrip images={galleryImages} maxVisible={3} />
            </Box>
          )}

          {/* Non-image files as download rows */}
          {nonImageFiles.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {nonImageFiles.map((file, index) => (
                <ReportFile
                  key={index}
                  file={{ name: file.name, content: file.content, size: file.size || 0 }}
                  fileIndex={index}
                  canDelete={false}
                  canDownload={true}
                  isNew={false}
                />
              ))}
            </Box>
          )}
        </>
      ) : fetchFailed || (!filesLoading && !files) ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FileIcon sx={{ fontSize: "16px", color: dt.textMuted }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted }}>
            {translate("serviceReports.fileCount", { count: fileCount })}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

const ExpandedSection: React.FC<ExpandedSectionProps> = ({ sr, isPhone, translate, navigate, isResolved }) => {
  const hasFileCount = (sr.fileCount ?? 0) > 0;
  const hasFilesArray = (sr.files?.length ?? 0) > 0;
  const hasFiles = hasFilesArray || hasFileCount;
  const hasNotes = Boolean(sr.notes);
  const hasDescription = Boolean(sr.description);

  return (
    <Box
      sx={{
        backgroundColor: "#FDFCFB",
        padding: isPhone ? "20px 12px" : "20px 24px",
      }}
    >
      {/* Thin 1px divider at the boundary — rendered via margin trick */}
      <Box
        sx={{
          height: "1px",
          backgroundColor: dt.cardBorder,
          mx: 0,
          mt: "-20px",
          mb: "20px",
        }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* DESCRIPTION block — blue left-bar accent (green for resolved) */}
        {hasDescription && (
          <Box>
            <SectionLabel color={isResolved ? "#2D8C4E" : undefined}>{translate("serviceReports.expanded.description")}</SectionLabel>
            <Box
              sx={{
                borderLeft: isResolved ? "3px solid #2D8C4E" : "3px solid #3498DB",
                borderRadius: dt.radiusSm,
                padding: isPhone ? "10px 12px" : "14px 16px",
                backgroundColor: isResolved ? "#F0FAF4" : dt.inputBg,
              }}
            >
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#4A4F57",
                  lineHeight: "160%",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {sr.description}
              </Box>
            </Box>
          </Box>
        )}

        {/* TECHNICAL NOTES block — blue left-bar accent, no name/initials */}
        {hasNotes && (
          <InlineNoteSection
            sr={sr}
            isPhone={isPhone}
            translate={translate}
            canAddNote={false}
            onAddNote={async () => {}}
          />
        )}

        {/* ATTACHMENTS */}
        {hasFiles && <AttachmentsSection sr={sr} translate={translate} />}

        {/* "View details →" link — bottom-right */}
        {sr.machineId && sr.number && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: "4px" }}>
            <Box
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                navigate(`/machinery/${sr.machineId}/service-reports/${sr.number}`);
              }}
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: "#D4572B",
                lineHeight: "16px",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline", color: "#B84A24" },
              }}
            >
              {translate("serviceReports.viewDetails")} {"\u2192"}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServiceReportCard;
